import { Box } from '@mui/material';
import React from 'react';

/**
 * Understanding the (value !== index) condition.
 *
 * Previously, this condition was not present and a hidden={value !== index} prop was used.
 * This caused a flash of the Datagrid's content when the tab was changed. The render sequence was:
 * 1. User clicks Tab 2
 * 2. CSS changes: Tab 1 gets hidden={true}, Tab 2 gets hidden={false}
 * 3. DataGrid detects visibility change via ResizeObserver
 * 4. DataGrid measures column widths (reads DOM) and recalculates layout
 * 5. DataGrid re-renders with correct widths
 *
 * With that new condition we skip the rendering of inactive tabs entirely:
 * 1. User clicks Tab 2
 * 2. React unmounts Tab 1's DataGrid (removed from DOM)
 * 3. React mounts Tab 2's DataGrid (added to DOM)
 * 4. DataGrid initializes with fresh, clean state, and renders once with correct widths
 */

export const TabPanel = ({ children, value, index, customStyle, ...other }) => {
  // Only render children when this tab is active
  // This unmounts inactive tabs, resetting their state but eliminating visual artifacts (ex: Datagrid's AutosizeOnMount)
  if (value !== index) {
    return null;
  }

  return (
    <Box
      aria-labelledby={`simple-tab-${index}`}
      id={`simple-tabpanel-${index}`}
      role="tabpanel"
      sx={{ height: '100%', ...customStyle }}
      {...other}
    >
      {children}
    </Box>
  );
};
